/**
 * IndexedDB service for persistent storage
 * Handles login state, conversations, contacts, and user data
 */

import logger from '../utils/logger';

const DB_NAME = 'TIConversationsApp';
const DB_VERSION = 2;  // Incremented to add avatar cache store

// Store names
const STORES = {
  LOGIN_STATE: 'loginState',
  CONVERSATIONS: 'conversations',
  CONTACTS: 'contacts',
  USERS: 'users',
  APP_STATE: 'appState',
  AVATAR_CACHE: 'avatarCache',  // New store for persistent avatar caching
};

class IndexedDBService {
  constructor() {
    this.db = null;
  }

  async init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Login state store
        if (!db.objectStoreNames.contains(STORES.LOGIN_STATE)) {
          db.createObjectStore(STORES.LOGIN_STATE, { keyPath: 'id' });
        }

        // Conversations store
        if (!db.objectStoreNames.contains(STORES.CONVERSATIONS)) {
          const conversationsStore = db.createObjectStore(STORES.CONVERSATIONS, { keyPath: 'id' });
          conversationsStore.createIndex('ownerId', 'ownerId', { unique: false });
          conversationsStore.createIndex('userId', 'users', { unique: false, multiEntry: true });
        }

        // Contacts store
        if (!db.objectStoreNames.contains(STORES.CONTACTS)) {
          const contactsStore = db.createObjectStore(STORES.CONTACTS, { keyPath: 'id' });
          contactsStore.createIndex('userId', 'userId', { unique: false });
        }

        // Users store
        if (!db.objectStoreNames.contains(STORES.USERS)) {
          db.createObjectStore(STORES.USERS, { keyPath: 'id' });
        }

        // App state store (for misc settings, counters, etc.)
        if (!db.objectStoreNames.contains(STORES.APP_STATE)) {
          db.createObjectStore(STORES.APP_STATE, { keyPath: 'key' });
        }

        // Avatar cache store - for persistent avatar caching
        if (!db.objectStoreNames.contains(STORES.AVATAR_CACHE)) {
          const avatarStore = db.createObjectStore(STORES.AVATAR_CACHE, {
            keyPath: 'userId',  // Primary key
          });

          // Index for efficient queries
          avatarStore.createIndex('mxcUrl', 'mxcUrl', { unique: false });
          avatarStore.createIndex('timestamp', 'timestamp', { unique: false });

          logger.debug('Avatar cache store created');
        }
      };
    });
  }

  // Generic CRUD operations
  async get(storeName, key) {
    const transaction = this.db.transaction([storeName], 'readonly');
    const store = transaction.objectStore(storeName);
    return new Promise((resolve, reject) => {
      const request = store.get(key);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async put(storeName, data) {
    const transaction = this.db.transaction([storeName], 'readwrite');
    const store = transaction.objectStore(storeName);
    return new Promise((resolve, reject) => {
      const request = store.put(data);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async delete(storeName, key) {
    const transaction = this.db.transaction([storeName], 'readwrite');
    const store = transaction.objectStore(storeName);
    return new Promise((resolve, reject) => {
      const request = store.delete(key);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getAll(storeName) {
    const transaction = this.db.transaction([storeName], 'readonly');
    const store = transaction.objectStore(storeName);
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getAllByIndex(storeName, indexName, value) {
    const transaction = this.db.transaction([storeName], 'readonly');
    const store = transaction.objectStore(storeName);
    const index = store.index(indexName);
    return new Promise((resolve, reject) => {
      const request = index.getAll(value);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async clear(storeName) {
    const transaction = this.db.transaction([storeName], 'readwrite');
    const store = transaction.objectStore(storeName);
    return new Promise((resolve, reject) => {
      const request = store.clear();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Login state methods
  async getLoginState() {
    return await this.get(STORES.LOGIN_STATE, 'current');
  }

  async setLoginState(loginData) {
    return await this.put(STORES.LOGIN_STATE, { id: 'current', ...loginData });
  }

  async clearLoginState() {
    return await this.delete(STORES.LOGIN_STATE, 'current');
  }

  // User-specific data methods
  async getUserConversations(userId) {
    return await this.getAllByIndex(STORES.CONVERSATIONS, 'userId', userId);
  }

  async getUserContacts(userId) {
    return await this.getAllByIndex(STORES.CONTACTS, 'userId', userId);
  }

  async saveConversation(conversation) {
    return await this.put(STORES.CONVERSATIONS, conversation);
  }

  async saveContact(contact) {
    return await this.put(STORES.CONTACTS, contact);
  }

  async saveUser(user) {
    return await this.put(STORES.USERS, user);
  }

  // Bulk operations for initial data seeding
  async seedUserData(userId, conversations, contacts) {
    const transaction = this.db.transaction([STORES.CONVERSATIONS, STORES.CONTACTS], 'readwrite');

    // Save conversations
    const conversationsStore = transaction.objectStore(STORES.CONVERSATIONS);
    for (const conversation of conversations) {
      conversationsStore.put(conversation);
    }

    // Save contacts
    const contactsStore = transaction.objectStore(STORES.CONTACTS);
    for (const contact of contacts) {
      // Ensure contact has userId for indexing
      const contactWithUserId = { ...contact, userId };
      contactsStore.put(contactWithUserId);
    }

    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }

  /**
   * Close the database connection
   */
  close() {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
  }

  /**
   * Delete the entire database
   * Used during logout to ensure complete data cleanup
   */
  async deleteDatabase() {
    try {
      // Close the connection first
      this.close();

      // Delete the database
      const deleteReq = indexedDB.deleteDatabase(DB_NAME);

      return new Promise((resolve, reject) => {
        deleteReq.onsuccess = () => {
          logger.debug(`Database ${DB_NAME} deleted successfully`);
          resolve();
        };
        deleteReq.onerror = () => {
          logger.error(`Failed to delete database ${DB_NAME}:`, deleteReq.error);
          reject(deleteReq.error);
        };
        deleteReq.onblocked = () => {
          logger.warn(`Delete of database ${DB_NAME} blocked - close all connections first`);
          // Still resolve as the database will be deleted once connections are closed
          resolve();
        };
      });
    } catch (error) {
      logger.error('Error deleting database:', error);
      throw error;
    }
  }

  /**
   * Avatar Cache Methods
   */

  /**
   * Get cached avatar for a user
   * @param {string} userId - Matrix user ID
   * @param {string} currentMxcUrl - Current MXC URL from Matrix SDK
   * @returns {Promise<Object|null>} - Cached avatar data or null
   */
  async getCachedAvatar(userId, currentMxcUrl) {
    try {
      if (!this.db) {
        logger.warn('IndexedDB not initialized');
        return null;
      }

      const transaction = this.db.transaction([STORES.AVATAR_CACHE], 'readonly');
      const store = transaction.objectStore(STORES.AVATAR_CACHE);

      return new Promise((resolve, reject) => {
        const request = store.get(userId);

        request.onsuccess = () => {
          const cached = request.result;

          // Validate cache
          if (!cached) {
            resolve(null);
            return;
          }

          // Check if MXC URL matches (avatar hasn't changed)
          if (cached.mxcUrl !== currentMxcUrl) {
            logger.debug(`Avatar cache invalid for ${userId} - MXC URL changed`);
            resolve(null);
            return;
          }

          // Optional: Check expiry (e.g., 365 days)
          const CACHE_EXPIRY = 365 * 24 * 60 * 60 * 1000; // 365 days in ms
          const age = Date.now() - cached.timestamp;
          if (age > CACHE_EXPIRY) {
            logger.debug(`Avatar cache expired for ${userId} - age: ${age}ms`);
            resolve(null);
            return;
          }

          logger.debug(`Avatar cache HIT for ${userId}`);
          resolve(cached);
        };

        request.onerror = () => {
          logger.error('Error reading avatar cache:', request.error);
          reject(request.error); // Reject on read errors
        };
      });
    } catch (error) {
      logger.error('Error in getCachedAvatar:', error);
      return null;
    }
  }

  /**
   * Cache avatar data for a user
   * @param {string} userId - Matrix user ID
   * @param {string} mxcUrl - Matrix MXC URL
   * @param {ArrayBuffer} blobData - Image binary data
   * @param {string} mimeType - Image MIME type
   */
  async cacheAvatar(userId, mxcUrl, blobData, mimeType) {
    try {
      if (!this.db) {
        logger.warn('IndexedDB not initialized');
        return null;
      }

      const transaction = this.db.transaction([STORES.AVATAR_CACHE], 'readwrite');
      const store = transaction.objectStore(STORES.AVATAR_CACHE);

      const avatarRecord = {
        userId,
        mxcUrl,
        blobData,
        mimeType,
        size: blobData.byteLength,
        timestamp: Date.now(),
      };

      return new Promise((resolve) => {
        const request = store.put(avatarRecord);

        request.onsuccess = () => {
          logger.debug(`Avatar cached for ${userId} (${blobData.byteLength} bytes)`);
          resolve();
        };

        request.onerror = () => {
          logger.error('Error caching avatar:', request.error);
          resolve(); // Non-blocking - resolve even on error
        };
      });
    } catch (error) {
      logger.error('Error in cacheAvatar:', error);
      return null; // Explicit return for consistency
    }
  }

  /**
   * Delete cached avatar for a user
   * @param {string} userId - Matrix user ID
   */
  async deleteCachedAvatar(userId) {
    try {
      if (!this.db) return null;

      return await this.delete(STORES.AVATAR_CACHE, userId);
    } catch (error) {
      logger.error('Error deleting cached avatar:', error);
      return null;
    }
  }

  /**
   * Clear all avatar cache
   */
  async clearAvatarCache() {
    try {
      if (!this.db) return null;

      logger.debug('Clearing all avatar cache');
      return await this.clear(STORES.AVATAR_CACHE);
    } catch (error) {
      logger.error('Error clearing avatar cache:', error);
      return null;
    }
  }

  /**
   * Get cache statistics
   */
  async getAvatarCacheStats() {
    try {
      if (!this.db) return { count: 0, totalSize: 0 };

      const transaction = this.db.transaction([STORES.AVATAR_CACHE], 'readonly');
      const store = transaction.objectStore(STORES.AVATAR_CACHE);

      return new Promise((resolve, reject) => {
        const request = store.getAll();

        request.onsuccess = () => {
          const cached = request.result || [];
          const totalSize = cached.reduce((sum, item) => sum + (item.size || 0), 0);

          resolve({
            count: cached.length,
            totalSize,
            totalSizeMB: (totalSize / (1024 * 1024)).toFixed(2),
          });
        };

        request.onerror = () => {
          logger.error('Error getting cache stats:', request.error);
          resolve({ count: 0, totalSize: 0, totalSizeMB: 0 });
        };
      });
    } catch (error) {
      logger.error('Error getting cache stats:', error);
      return { count: 0, totalSize: 0, totalSizeMB: 0 };
    }
  }
}

// Create singleton instance
const indexedDBService = new IndexedDBService();

export default indexedDBService;
export { STORES };
